/**
 * The user-facing sentence out of a failed job's `errorLogs`, or `null`.
 *
 * `completeFailure` (libs/common/src/job.ts) stores `String(err.stack ?? err)`,
 * a JavaScript stack trace. The first line of a thrown `Error`'s stack is
 * `Error: <message>`.
 *
 * **Fix round 1.** The first version of this function was a DENY-list —
 * reject anything shaped like a stack frame, a path, a URI, too long, no
 * terminal punctuation. A reviewer threw 38 adversarial inputs at it and 20
 * came back verbatim: absolute paths behind punctuation, Windows paths
 * (`C:\Users\...`), UNC paths, internal hostnames, an `ip:port` pair, an
 * email address, a JWT, an AWS access key id, and a scheme-less S3-object-key
 * shaped string. A deny-list is only ever as complete as the patterns
 * someone thought to write; it cannot be finished.
 *
 * This is now what the doc comment always claimed it was: an ALLOW-list of
 * the literal sentences our own workers throw. Three tasks in this feature
 * each wrote a deliberately-worded, actionable `throw new Error('...')` for
 * a specific worker exit code — a surface-orientation mismatch, an invalid
 * alignment, an OOM. Those strings (`KNOWN_SENTENCES` below) are the entire
 * vocabulary this function can ever return. A first line that is not
 * byte-for-byte one of them returns `null`, and the caller falls back to the
 * generic localized toast — never wrong, only unhelpful. Nothing not already
 * in this list can come out of this function: not a path, a key, a
 * hostname, or anything else, because it never returns anything it did not
 * already know about.
 *
 * None of the registered sentences carry a runtime-interpolated tail (see
 * the throw sites cited below — all are plain string literals, some
 * `+`-concatenated across lines but still fully literal), so exact string
 * equality is enough. If a future sentence has a variable tail, do not
 * register it via a bare prefix match unless the stable prefix is long
 * enough to be unmistakable AND the variable part cannot itself carry a
 * path/key/host — otherwise it re-opens exactly the hole this fix closes.
 *
 * **Deliberately excluded** — see `EXCLUDED_SENTENCES` below for the two
 * sentences excluded on purpose, each with its one-line reason. Every
 * `Worker did not produce <file>` / `job not found` throw in all three
 * workers is excluded too, but not listed individually there: those are
 * internal assertion failures outside any exit-code branch, not sentences
 * written for a customer, and `jobErrorMessageRegistry.test.ts`'s drift
 * check only scans exit-code-mapped throws (the ones a worker crafted
 * on purpose for a specific failure), not every throw in the file.
 *
 * **Fix round 2 (reviewer N1).** The six sentences below used to live in
 * four unsynchronized places — three worker throw sites plus this list plus
 * ad-hoc copies in two test files — with nothing checking them against each
 * other. A punctuation-level reword of any sentence, done as an innocent
 * copy edit, would have left every client test green while silently
 * disabling that sentence forever (a stale registry fails silently; the old
 * deny-list at least failed loudly by leaking). `KNOWN_SENTENCES` and
 * `EXCLUDED_SENTENCES` are now exported and cross-checked against the
 * worker source on disk by `jobErrorMessageRegistry.test.ts` — a reword
 * anywhere fails a test instead of silently doing nothing.
 *
 * Known limitation, unchanged from before: these sentences are English-only.
 * The workers throw English; translating them would need a message
 * catalogue spanning Python, two Node workers and five locales. A specific
 * English sentence beats a localized "it failed" for someone who has to act
 * on it, and the localized fallback still covers every failure we did not
 * word ourselves.
 */

// Verbatim from the throw sites — grep `throw new Error(` in
// processDeviation/index.ts, processSurfaceDifference/index.ts and
// processClearance/index.ts. Keep this list and those throw sites in sync;
// nothing type-checks across that boundary.
export const KNOWN_SENTENCES: ReadonlySet<string> = new Set([
  // processDeviation/index.ts exit 4, processSurfaceDifference/index.ts exit 4
  'The alignment supplied for station volumes is invalid — re-run with a different alignment or station range.',
  // processDeviation/index.ts exit 7
  'The design surface and the measured cloud look to be the wrong way round: a thickness was expected but the cloud sits mostly above the design. Check which layer is which and run again.',
  // processDeviation/index.ts exit 8
  'Horizontal mode measures the distance to vertical surfaces, so it has no vertical volume. Re-run in vertical, normal or nearest mode to get volume statistics or station volumes.',
  // processSurfaceDifference/index.ts exit 3
  'The two surfaces do not overlap in XY — nothing to compare.',
  // processSurfaceDifference/index.ts exit 5
  'The two surfaces look to be the wrong way round: a thickness was expected but the upper surface is mostly below the lower one. Swap them and run again.',
  // processClearance/index.ts exit 4
  'The clearance check could not run: the profile, the alignment or the station range is invalid, or the point cloud does not overlap the swept envelope. Check that the cloud and the alignment share a coordinate reference system, then re-run.',
  // processClearance/index.ts exit 6
  'The clearance worker ran out of memory while scoring the cloud. Re-run with a shorter station range, or contact support to raise the task size.',
]);

/**
 * Sentences deliberately NOT in `KNOWN_SENTENCES`, each with the one-line
 * reason it stays out. This is where an omission gets recorded as a
 * decision instead of an accident (reviewer N1) — recording it here, rather
 * than only in prose, lets `jobErrorMessageRegistry.test.ts` cross-check it
 * against the worker source, so a future reword of an excluded sentence
 * (which would otherwise pass every test silently) also fails a test.
 *
 * The clearance entry is a literal Node `throw new Error(...)` argument and
 * is verified against `processClearance/index.ts` directly. The deviation
 * entry is a Python `argparse.parser.error(...)` string with no Node-side
 * throw site at all (see the doc comment above) — it is verified against
 * `scripts/deviation.py` instead, in the same test file.
 */
export const EXCLUDED_SENTENCES: ReadonlyMap<string, string> = new Map([
  [
    'The clearance worker was started with invalid arguments (check the CLEARANCE_WORKERS / CLEARANCE_CHUNK task overrides).',
    'processClearance/index.ts exit 2 — names our own ECS task-override env vars (CLEARANCE_WORKERS / CLEARANCE_CHUNK); only reachable through a bad override, nothing a customer can act on. (Reviewer finding 13.)',
  ],
  [
    'A two-surface expectation is not available on a deviation run: the reference is already one surface. Use a set thickness, or run a surface difference instead.',
    "scripts/deviation.py argparse parser.error, deviation exit 2 — processDeviation/index.ts deliberately does not map exit 2 to its own throw (task-8's brief); the sentence only ever reaches errorLogs buried past line 1 of the generic spawn-failure message, so there is no first-line-matchable Node literal to register.",
  ],
]);

export function jobErrorMessage(errorLogs: string | null | undefined): string | null {
  if (!errorLogs) return null;
  const first = errorLogs.split('\n', 1)[0].trim();
  if (!first.startsWith('Error: ')) return null;

  const msg = first.slice('Error: '.length).trim();
  return KNOWN_SENTENCES.has(msg) ? msg : null;
}
